import { responsiveStyle } from '@synoptic/ui-kit/responsive/responsive-style.js';
import { themeVars } from '@synoptic/ui-kit/theme/vars.js';
import { borderRadius, space } from '@synoptic/ui-kit/tokens.js';
import { typography } from '@synoptic/ui-kit/typography.js';
import { createVar, globalStyle, style } from '@vanilla-extract/css';
import { cardContent } from '../ct-stats.$username/cards.css';
import { ctWrappedCardAppearence } from '../ct-stats.$username/route.css';

export const ctWrappedContent = style([
  {
    display: 'flex',
    justifyContent: 'space-between',
    flexDirection: 'column',
    width: '100%',
    flexGrow: 1,
  },
  responsiveStyle({
    mobile: {
      marginTop: space.s32,
      gap: space.s64,
      flexDirection: 'column-reverse',
    },
    tablet: {
      marginTop: 96,
      gap: 160,
      flexDirection: 'column',
    },
  }),
]);

export const ctWrappedHero = style([
  {
    display: 'flex',
    justifyContent: 'space-between',
    gap: space.s16,
    alignItems: 'start',
  },
  responsiveStyle({
    mobile: {
      flexDirection: 'column',
    },
    tablet: {
      flexDirection: 'row',
    },
  }),
]);

export const ctWrappedHeroMainContainer = style({
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'space-between',
  alignItems: 'start',
  alignSelf: 'stretch',
  gap: space.s24,
});

export const ctWrappedHeroHeadingContainer = style({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'flex-start',
  gap: space.s12,
  alignSelf: 'stretch',
});

export const ctWrappedHeroHeading = style([
  {
    color: themeVars.container.foreground.primary,
  },
  responsiveStyle({
    mobile: {
      ...typography.headlineMd,
    },
    tablet: {
      ...typography.displayMd,
    },
  }),
]);

export const ctWrappedHeroSubheading = style([
  typography.bodyLg,
  {
    color: themeVars.container.foreground.secondary,
  },
]);

export const ctWrappedHeroCTAContainer = style([
  {
    display: 'flex',
    gap: space.s16,
    width: '100%',
  },
  responsiveStyle({
    mobile: {
      flexDirection: 'column',
    },
    tablet: {
      flexDirection: 'row',
    },
  }),
]);

export const ctWrappedInput = style([
  {
    flexGrow: 1,
  },
]);

export const ctaWrappedSearchList = style({
  selectors: {
    '&:is(&)': {
      backgroundColor: 'rgba(255, 255, 255, 0.02)',
      outlineColor: 'rgba(221, 212, 245, 0.2)',
      backdropFilter: 'blur(27.5px)',
    },
  },
});

export const ctWrappedAbout = style([
  {
    borderRadius: borderRadius.r16,
    border: `1px solid rgba(221, 212, 245, 0.20)`,
    backgroundColor: 'rgba(255, 255, 255, 0.02)',
    backdropFilter: 'blur(27.5px)',
    padding: space.s16,
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    width: '100%',
    gap: space.s12,
  },
  responsiveStyle({
    mobile: {
      maxWidth: '100%',
    },
    tablet: {
      maxWidth: 314,
    },
  }),
]);

export const ctWrappedAboutTextContainer = style({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'flex-start',
  gap: space.s10,
});

export const ctWrappedAboutTitle = style([
  typography.titleSm,
  {
    color: themeVars.container.foreground.primary,
  },
]);

export const ctWrappedAboutDescription = style([
  typography.bodySm,
  {
    color: themeVars.container.foreground.secondary,
  },
]);

export const ctWrappedAboutLink = style({
  color: themeVars.container.foreground.secondary,
  ':hover': {
    textDecoration: 'underline',
  },
});

export const ctWrappedAboutExploreButton = style({
  selectors: {
    '&:is(&)': {
      padding: '4px',
      color: themeVars.container.foreground.primary,
    },
  },
});

export const ctWrappedCards = style([
  {
    display: 'flex',
    position: 'relative',
    flexGrow: 1,
    maxHeight: 702,
  },
  responsiveStyle({
    mobile: {
      marginBottom: 0,
      minHeight: 200,
    },
    tablet: {
      minHeight: 0,
      marginBottom: -32,
      //  guard to avoid white spots from backdrop-filter
      ':before': {
        position: 'absolute',
        content: '',
        display: 'block',
        height: '32px',
        left: '-32px',
        right: '-32px',
        bottom: '-32px',
        zIndex: 0,
        backgroundColor: '#050110',
      },

      // hide overflowing cards
      ':after': {
        position: 'absolute',
        content: '',
        display: 'block',
        height: '32px',
        left: '-32px',
        right: '-32px',
        bottom: '-32px',
        zIndex: 10,
        backgroundColor: '#f5f4fd',
      },
    },
  }),
]);

const cardWidthVar = createVar('cardWidth');
const cardCountVar = createVar('cardCount');
const activeTransformVar = createVar('activeTransform');

const getOffset = (index: number) => {
  return `calc(calc(calc(100% - min(${cardWidthVar}, 92%)) / ${cardCountVar}) * ${index})`;
};

export const ctWrappedCardPlaceholder = style([
  ctWrappedCardAppearence,

  responsiveStyle({
    mobile: {
      height: '100%',
      vars: {
        [cardWidthVar]: '100%',
        [cardCountVar]: '3',
        [activeTransformVar]: 'rotate(-5deg) translate(-2px, -10px)',
      },
    },
    tablet: {
      height: 'calc(100% + 32px)',
      ':hover': {
        transform: activeTransformVar,
      },
      vars: {
        [cardWidthVar]: 'max(40%, 500px)',
        [cardCountVar]: '11',
        [activeTransformVar]: 'rotate(-5deg) translateY(-80px)',
      },

      selectors: {
        '&[data-card="intro"]': {
          minHeight: 600,
        },
      },
    },
  }),

  {
    flexShrink: 0,
    width: cardWidthVar,

    position: 'absolute',
    transformOrigin: 'right top',
    transition: 'transform 0.3s ease-out',

    selectors: {
      '&[data-card="intro"]': {
        containerType: 'inline-size',

        viewTransitionName: 'ct-wrapped-card',

        zIndex: 11,
        left: `${getOffset(0)}`,
        transform: activeTransformVar,
      },
      '&[data-card="bridge"]': {
        zIndex: 10,
        left: `${getOffset(1)}`,
      },
      '&[data-card="communities"]': {
        zIndex: 9,
        left: `${getOffset(2)}`,
      },
      '&[data-card="reach"]': {
        zIndex: 8,
        left: `${getOffset(3)}`,
      },
      '&[data-card="flow"]': {
        zIndex: 7,
        left: `${getOffset(4)}`,
      },
      '&[data-card="mutuals"]': { zIndex: 6, left: `${getOffset(5)}` },
      '&[data-card="regulars"]': {
        zIndex: 5,
        left: `${getOffset(6)}`,
      },
      '&[data-card="heavyweights"]': { zIndex: 4, left: `${getOffset(7)}` },
      '&[data-card="loudestHits"]': {
        zIndex: 3,
        left: `${getOffset(8)}`,
      },
      '&[data-card="replies"]': { zIndex: 2, left: `${getOffset(9)}` },
      '&[data-card="words"]': {
        zIndex: 1,
        left: `${getOffset(10)}`,
      },
      '&[data-card="final"]': { zIndex: 0, left: `${getOffset(11)}` },
    },
  },
]);

export const ctWrappedExtraPlaceholders = style(
  responsiveStyle({
    mobile: {
      display: 'none',
    },
    tablet: {
      display: 'contents',
    },
  }),
);

globalStyle(`${ctWrappedCardPlaceholder} ${cardContent}`, {
  overflow: 'hidden',
  // fontSize: '2cqw',
});
